import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { CycleCountApi } from 'api/cycleCount';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { formatUtcToLocalTimeString } from 'utils/dateUtils/ledgerDateUtils';

import { ReplenCycleCountForm } from './replenCycleCountForm';

const cycleCountApi = new CycleCountApi();

function ReplenCycleCountTab() {
    const { user } = useContext(UserContext);
    const [rows, setRows] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [requestIdToCancel, setRequestIdToCancel] = useState(null);

    const getReplenishmentCycleCountRows = async () => {
        const res = await cycleCountApi.getIncompleteReplenishmentLocationCycleCountRequests(user.facilityID);
        if (!res.status) {
            toast.error(res.msg || 'Failed to load replenishment cycle counts');
            return;
        }

        setRows(res.data.requests);
    };

    useEffect(() => {
        getReplenishmentCycleCountRows();
    }, []);

    const handleCreateSuccess = () => {
        toast.success('Cycle count request created');
        setModalOpen(false);
        getReplenishmentCycleCountRows();
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleOpenCancelModal = (requestId) => {
        setRequestIdToCancel(requestId);
        setCancelModalOpen(true);
    };

    const handleCloseCancelModal = () => {
        setCancelModalOpen(false);
        setRequestIdToCancel(null);
    };

    const handleCancel = async () => {
        const res = await cycleCountApi.cancelCycleCountRequest(requestIdToCancel);
        if (!res.status) {
            toast.error(res.msg || 'Failed to cancel cycle count request');
            setCancelModalOpen(false);
            setRequestIdToCancel(null);
            return;
        }
        toast.success('Cycle count request cancelled');
        setCancelModalOpen(false);
        setRequestIdToCancel(null);
        getReplenishmentCycleCountRows();
    };

    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <div>
            <div className="flex justify-end mb-4">
                <Button onClick={() => setModalOpen(true)}>Create New</Button>
            </div>
            <div className="mb-3 text-sm text-muted-foreground">
                Showing incomplete replenishment cycle counts (status NEW or IN PROGRESS).
            </div>
            <ReplenCycleCountForm open={modalOpen} onSuccess={handleCreateSuccess} closeModal={handleCloseModal} />
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>ID</TableHead>
                            <TableHead>Date Requested</TableHead>
                            <TableHead>Date Started</TableHead>
                            <TableHead>Manager</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Num Child Locations</TableHead>
                            <TableHead>Completed Locations</TableHead>
                            <TableHead>Cancel</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedRows.length > 0 ? (
                            paginatedRows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell className="text-left">{row.id}</TableCell>
                                    <TableCell className="text-left">
                                        {row.createdAt ? formatUtcToLocalTimeString(row.createdAt) : ''}
                                    </TableCell>
                                    <TableCell className="text-left">
                                        {row.startedAt ? formatUtcToLocalTimeString(row.startedAt) : ''}
                                    </TableCell>
                                    <TableCell className="text-left">{row.createdBy}</TableCell>
                                    <TableCell className="text-left">{row.status}</TableCell>
                                    <TableCell className="text-left">{row.locationName}</TableCell>
                                    <TableCell className="text-left">{row.numChildLocations}</TableCell>
                                    <TableCell className="text-left">
                                        {row.numCompletedLocations} / {Number(row.numChildLocations) + 1}
                                    </TableCell>
                                    <TableCell className="text-left">
                                        {['NEW', 'IN PROGRESS'].includes(row.status) && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleOpenCancelModal(row.id)}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={9} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel Cycle Count Request</DialogTitle>
                        <DialogDescription>Are you sure you want to cancel this cycle count request?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={handleCloseCancelModal}>
                            No, Keep It
                        </Button>
                        <Button onClick={handleCancel}>Yes, Cancel Request</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <div className="flex items-center justify-end space-x-2 py-4">
                <Button variant="outline" size="sm" onClick={() => handleChangePage(page - 1)} disabled={page === 0}>
                    Previous
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChangePage(page + 1)}
                    disabled={page >= Math.ceil(rows.length / rowsPerPage) - 1}
                >
                    Next
                </Button>
            </div>
        </div>
    );
}

export default ReplenCycleCountTab;
